import type { ReactNode } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import { useIsPhone } from "../hooks/useIsPhone";
import { PhoneList, type PhoneSlot } from "./phone/PhoneList";
import type { OverflowAction } from "./phone/OverflowMenu";

export interface Column<T> {
  id: string;
  label: string;
  align?: "left" | "right" | "center";
  /** Fixed column width (CSS value, e.g. "40%" or "120px"). */
  width?: string;
  render: (row: T) => ReactNode;
  /** Where the column goes in the phone list (below 900 px). See PhoneList. */
  phone?: PhoneSlot;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  loading: boolean;
  error?: Error | null;
  emptyMessage?: string;
  ariaLabel: string;
  /** Returns a unique key for each row. */
  getRowKey: (row: T) => string;
  /** Called when a row is clicked. If omitted, rows are not clickable. */
  onRowClick?: (row: T) => void;
  /** Phone only: actions for each row's ⋮ menu (desktop shows them as columns). */
  phoneRowActions?: (row: T) => OverflowAction[];
  /** Phone only: accessible name for a row's ⋮ button. */
  phoneRowActionsLabel?: (row: T) => string;
}

export function DataTable<T>({
  columns,
  data,
  loading,
  error,
  emptyMessage = "No data found.",
  ariaLabel,
  getRowKey,
  onRowClick,
  phoneRowActions,
  phoneRowActionsLabel,
}: DataTableProps<T>) {
  const isPhone = useIsPhone();

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        Could not load {ariaLabel.toLowerCase()}. {error.message}
      </Alert>
    );
  }

  if (data.length === 0) {
    return <Typography color="text.secondary">{emptyMessage}</Typography>;
  }

  if (isPhone) {
    return (
      <PhoneList
        columns={columns}
        data={data}
        ariaLabel={ariaLabel}
        getRowKey={getRowKey}
        onRowClick={onRowClick}
        rowActions={phoneRowActions}
        rowActionsLabel={phoneRowActionsLabel}
      />
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table aria-label={ariaLabel} sx={columns.some((c) => c.width) ? { tableLayout: "fixed" } : undefined}>
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell key={col.id} align={col.align} sx={col.width ? { width: col.width } : undefined}>
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row) => (
            <TableRow
              key={getRowKey(row)}
              data-testid={`table-row-${getRowKey(row)}`}
              hover={!!onRowClick}
              sx={onRowClick ? { cursor: "pointer" } : undefined}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              {columns.map((col) => (
                <TableCell key={col.id} align={col.align}>
                  {col.render(row)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
